import React, { useState } from "react";
import SupplierCodeIcon from "../../../../svgs/supplier-code.svg";
import SupplierAddressIcon from "../../../../svgs/supplier-address.svg";
import SupplierPOCIcon from "../../../../svgs/supplier-poc.svg";
import SupplierNumberIcon from "../../../../svgs/supplier-number.svg";
import BuildingIcon from "../../../../svgs/building.svg";
import { useNotification } from "../../../../../Notifications/NotificationProvider";
import { InventoryService } from "../../../../network/InventoryService";
import FormField from "../../../../../Shared/Form/FormField";

const SupplierCreateModal = ({
  showSupplierCreateModal,
  setShowSupplierCreateModal,
  onSupplierCreated,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    name: "",
    code: "",
    poc_name: "",
    contact_number: "",
    address: "",
    description: "",
  });
  const [formError, setFormError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "description" && value.length > 300) return;

    if (name === "contact_number") {
      const filteredValue = value.replace(/[^0-9+]/g, "");
      setForm({ ...form, [name]: filteredValue });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.name ||
      !form.code ||
      !form.poc_name ||
      !form.contact_number ||
      !form.address
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    setIsCreating(true);
    setFormError("");

    try {
      const supplierData = {
        name: form.name,
        code: form.code,
        poc_name: form.poc_name,
        contact_number: form.contact_number,
        address: form.address,
        description: form.description,
      };

      const response = await InventoryService.createSupplier(supplierData);

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Supplier created successfully!",
        });

        setForm({
          name: "",
          code: "",
          poc_name: "",
          contact_number: "",
          address: "",
          description: "",
        });

        setShowSupplierCreateModal(false);

        if (onSupplierCreated) {
          onSupplierCreated();
        }
      } else {
        throw new Error(response.message || "Failed to create supplier");
      }
    } catch (error) {
      console.error("Error creating supplier:", error);

      let errorMessage = "Failed to create supplier. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (!showSupplierCreateModal) return null;

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() => !isCreating && setShowSupplierCreateModal(false)}
    >
      <form
        className="inventory-modal supplier-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="inventory-modal-header">
          <h3>Add New Supplier</h3>
          <button
            className="close-btn"
            type="button"
            onClick={() => !isCreating && setShowSupplierCreateModal(false)}
            disabled={isCreating}
          >
            ×
          </button>
        </div>

        <div className="inventory-modal-body">
          <FormField
            id="supplier-name"
            name="name"
            title="Supplier Name"
            type="text"
            icon={BuildingIcon}
            value={form.name}
            onChange={handleInputChange}
            placeholder="Enter supplier name..."
            disabled={isCreating}
            required
          />

          <FormField
            id="supplier-code"
            name="code"
            title="Supplier Code"
            type="text"
            icon={SupplierCodeIcon}
            value={form.code}
            onChange={handleInputChange}
            placeholder="Enter supplier code..."
            disabled={isCreating}
            required
          />

          <FormField
            id="poc-name"
            name="poc_name"
            title="Supplier POC"
            type="text"
            icon={SupplierPOCIcon}
            value={form.poc_name}
            onChange={handleInputChange}
            placeholder="Enter POC name..."
            disabled={isCreating}
            required
          />

          <FormField
            id="contact-number"
            name="contact_number"
            title="Supplier POC Contact Number"
            type="tel"
            icon={SupplierNumberIcon}
            value={form.contact_number}
            onChange={handleInputChange}
            placeholder="Enter contact number..."
            disabled={isCreating}
            required
          />

          <FormField
            id="address"
            name="address"
            title="Supplier Address"
            type="text"
            icon={SupplierAddressIcon}
            value={form.address}
            onChange={handleInputChange}
            placeholder="Enter supplier address..."
            disabled={isCreating}
            required
          />

          <FormField
            id="description"
            name="description"
            title="Supplier Description"
            type="textarea"
            value={form.description}
            onChange={handleInputChange}
            placeholder="Enter supplier description..."
            disabled={isCreating}
          />

          {formError && <div className="inventory-form-error">{formError}</div>}
        </div>

        <div className="inventory-modal-footer">
          <button
            className="cancel-btn"
            type="button"
            onClick={() => setShowSupplierCreateModal(false)}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button className="submit-btn" type="submit" disabled={isCreating}>
            {isCreating ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierCreateModal;
